import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, LockOpen } from "lucide-react";

export const useLockedAccounts = (enabled = true) => {
  const [locked, setLocked] = useState({});
  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.getLockedAccounts();
      setLocked(Object.fromEntries((data.locked || []).map((l) => [l.user_id, l])));
    } catch {
      setLocked({});
    }
  }, [enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return { locked, refresh };
};

const untilLabel = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export const LockedBadge = ({ lock, userId }) => (
  <Badge variant="outline" className="ml-2 border-red-300 bg-red-50 text-red-700" data-testid={`locked-badge-${userId}`}
    title={`Bloqué jusqu'à ${untilLabel(lock.locked_until)}`}>
    <Lock className="h-3 w-3 mr-1" /> Bloqué jusqu'à {untilLabel(lock.locked_until)}
  </Badge>
);

export const UnlockButton = ({ userId, email, onDone }) => {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try {
      await api.unlockAccount(userId);
      toast.success(`Compte ${email} débloqué`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err, "Déblocage impossible"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="ghost" size="icon" className="h-8 w-8" title="Débloquer le compte" onClick={unlock} disabled={busy}
      data-testid={`unlock-account-${userId}`}>
      <LockOpen className="h-4 w-4 text-red-600" />
    </Button>
  );
};
